import { native } from '@forest/native-services'
import { cn } from '@forest/shared'
import { useStore } from '@nanostores/react'
import { motion } from 'motion/react'
import { useState } from 'react'
import leyaBell from '~/assets/images/leya-bell.png'
import { m } from '~/paraglide/messages'
import { language } from '~/params'
import {
  formatProjectedReminderDate,
  projectedReminderDate,
  REMINDER_LEAD_DAY_OPTIONS,
  type ReminderLeadDays,
} from '~/features/multi-page/lib/trial-reminder'
import { $products } from '~/store/products'
import { $reminderLeadDays } from '~/store/reminder-lead-days'

// The Lokalise keys start with a digit, so Paraglide exports them under their
// raw names rather than as dotted identifiers.
const optionLabel: Record<ReminderLeadDays, () => string> = {
  2: m['2_days'],
  3: m['3_days'],
}

/**
 * Variant1 of the trial reminder day selection experiment: the user picks the
 * reminder lead time (2 or 3 days before trial end) before seeing the plans.
 * The choice lives in `$reminderLeadDays` for the post-purchase flow.
 */
export function ReminderDaySelectionPage(props: { next: () => void }) {
  // The CTA only works once the page has finished appearing, and only once:
  // the same guard the Control page keeps in a ref, held as state here so the
  // button's disabled state reflects it.
  const [isEntranceDone, setIsEntranceDone] = useState(false)
  const products = useStore($products)
  const selectedLeadDays = useStore($reminderLeadDays)

  const handleContinue = () => {
    if (selectedLeadDays === null) return
    void native.general.logEvent({
      event_name: 'component_event',
      event_params: {
        type: 'btn',
        app_action: 'click',
        component_name: `trial_reminder_${selectedLeadDays}_days`,
      },
    })
    props.next()
    setIsEntranceDone(false)
  }

  if (!products) return null
  const trialDays = products.annualEarlyBird.eligible_trial_days
  const today = new Date()

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative mx-auto h-full max-w-[640px] space-y-6 overflow-y-auto px-8 pb-48 pt-[120px]"
      onAnimationComplete={() => {
        setIsEntranceDone(true)
      }}
    >
      <h1
        id="reminder-day-selection-title"
        className="whitespace-pre-line px-4 text-center text-2xl font-bold text-white"
        dangerouslySetInnerHTML={{
          __html:
            selectedLeadDays === null
              ? m.trial_reminder_title_1()
              : m.trial_reminder_title_2({ number: selectedLeadDays }),
        }}
      />

      {/* Preloaded from index.html so it is ready before page 3 renders. */}
      <img
        src={leyaBell}
        alt=""
        className="mx-auto h-[32vh] w-auto max-w-full object-contain"
      />

      <div
        role="radiogroup"
        aria-labelledby="reminder-day-selection-title"
        className="space-y-3"
      >
        {REMINDER_LEAD_DAY_OPTIONS.map((leadDays) => (
          <ReminderLeadDaysOption
            key={leadDays}
            label={optionLabel[leadDays]()}
            date={formatProjectedReminderDate(
              projectedReminderDate({ now: today, trialDays, leadDays }),
              language,
            )}
            selected={selectedLeadDays === leadDays}
            onSelect={() => $reminderLeadDays.set(leadDays)}
          />
        ))}
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 bg-[#001721] px-6 pb-12 pt-3"
        style={{
          boxShadow: '0 -3px 3px 0 rgba(255, 255, 255, 0.20)',
        }}
      >
        <div className="mx-auto max-w-[640px] space-y-3">
          <p className="text-center text-[#FFFFFFE6]">
            {m.subscription_walkthrough_p1_trial_fee()}
          </p>

          <button
            className={cn(
              'group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]',
              // Dimmed for "nothing chosen yet", not for the entrance guard,
              // so the page does not flash as it appears.
              selectedLeadDays === null && 'opacity-40',
            )}
            disabled={!isEntranceDone || selectedLeadDays === null}
            onClick={handleContinue}
          >
            <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
              {m.subscription_view_complete_benefit()}
            </span>
          </button>
        </div>
      </div>
    </motion.div>
  )
}

function ReminderLeadDaysOption(props: {
  label: string
  date: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      onClick={props.onSelect}
      className={cn(
        // Exactly as tall as the CTA below, and the stroke is drawn inside
        // that box, so selecting a row never shifts the layout.
        'flex h-[52px] w-full items-center justify-between gap-4 rounded-xl px-4 text-left text-white',
        props.selected ? 'gradient-stroke bg-white/40' : 'bg-white/10',
      )}
    >
      <span className="truncate text-lg font-bold">{props.label}</span>
      <span className="shrink-0 text-base">{props.date}</span>
    </button>
  )
}
